import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInstagram } from 'react-icons/fa';
import { MdContentCut, MdPhotoLibrary, MdInfoOutline, MdEvent } from 'react-icons/md';
import type { NavigationProps, MenuItem } from './types';

// Icon mapper for menu items
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  MdContentCut,
  MdPhotoLibrary,
  MdInfoOutline,
  MdEvent,
};

const NavigationMobile: React.FC<NavigationProps> = ({
  data,
  onLogoClick,
  onMenuItemClick,
  className = '',
}) => {
  if (!data) throw new Error('NavigationMobile component requires data prop');

  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  /* -------------------- Close Menu on Route Change -------------------- */
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  /* -------------------- Lock Body Scroll When Menu Open -------------------- */
  useEffect(() => {
    if (isMenuOpen) {
      // Simple overflow hidden - no stacking context issues
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  /* -------------------- Scroll To Section -------------------- */
  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const yOffset = -80;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  /* -------------------- Menu Click -------------------- */
  const handleMenuClick = (item: MenuItem) => {
    // Close menu first
    setIsMenuOpen(false);

    // Check if onMenuItemClick handled it (returns true)
    if (onMenuItemClick) {
      const handled = onMenuItemClick(item);
      if (handled) return; // Stop if handler returned true
    }
    if (item.onClick) return item.onClick();
    if (!item.href) return;

    if (item.href.startsWith('/#')) {
      const id = item.href.replace('/#', '');

      if (location.pathname === '/') {
        // Wait for menu close animation (300ms) + buffer
        setTimeout(() => scrollToSection(id), 350);
      } else {
        navigate('/');
        // Wait longer for navigation + menu close
        setTimeout(() => scrollToSection(id), 500);
      }
    } else {
      navigate(item.href);
    }
  };

  /* -------------------- Logo Click -------------------- */
  const handleLogoClickDefault = () => {
    if (onLogoClick) return onLogoClick();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-[100] bg-black shadow-lg border-b border-white/10 ${className}`}
    >
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4">
        {/* Logo */}
        <button
          onClick={handleLogoClickDefault}
          className="flex items-center gap-3 cursor-pointer"
          aria-label="Go to homepage"
        >
          <img 
            src="/logo.jpg" 
            alt="Barber Innovations Logo" 
            className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover shadow-md"
          />

          <div className="text-white">
            <h1 className="font-bold tracking-widest text-base sm:text-lg">
              {data.logo.text}
            </h1>
            <p className="uppercase text-white/80 tracking-[0.25em] text-[10px] sm:text-xs">
              {data.logo.subtext}
            </p>
          </div>
        </button>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsMenuOpen(p => !p)}
          className="relative z-[1000] text-white text-3xl"
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Menu - Full Screen */}
      <AnimatePresence mode="wait">
        {isMenuOpen && (
          <motion.div
            id="mobile-menu"
            role="navigation"
            aria-hidden={!isMenuOpen}
            className="fixed left-0 right-0 bottom-0 top-[80px] bg-black overflow-y-auto z-[9999]"
            style={{ WebkitOverflowScrolling: 'touch' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
            }}
          >
            <motion.ul
              className="flex flex-col px-5 py-6 gap-2 text-white min-h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {/* Phone Number */}
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  duration: 0.3,
                  delay: 0,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <a
                  href="tel:+17325816834"
                  className="flex items-center gap-3 w-full py-3 px-4 rounded-xl hover:bg-white/10 transition bg-primary/20"
                >
                  <span className="material-symbols-outlined text-2xl">phone</span>
                  <span className="font-semibold">(732) 581-6834</span>
                </a>
              </motion.li>
              {data.menuItems.map((item, index) => {
                const IconComponent = item.icon ? iconMap[item.icon] : null;
                
                return (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  >
                    <button
                      onClick={() => handleMenuClick(item)}
                      className={`flex items-center gap-3 w-full text-left py-3 px-4 rounded-xl transition ${
                        item.highlight
                          ? 'bg-primary text-white font-semibold text-center justify-center'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      {IconComponent && <IconComponent className="text-xl" />}
                      {item.label}
                    </button>
                  </motion.li>
                );
              })}
              {/* Instagram Link */}
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  duration: 0.3,
                  delay: data.menuItems.length * 0.05,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <a
                  href="https://www.instagram.com/Greg_thebarber7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full py-3 px-4 rounded-xl hover:bg-white/10 transition"
                >
                  <FaInstagram className="text-2xl" />
                  <span>Instagram</span>
                </a>
              </motion.li>
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavigationMobile;
